import { useState } from "react";
import { BadgeCheck, ClipboardCheck, XCircle } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState, StatCard } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { money } from "./SalesWorkbench";
import { useSales, useSalesActions } from "./useSales";

export function OrderApprovalPage() {
  const { session } = useAuth();
  const organization = useOrganization();
  const sales = useSales();
  const actions = useSalesActions();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [reasons, setReasons] = useState<Record<string, string>>({});
  if (!session || !organization.data || !sales.data) return <PageSkeleton />;
  const orders = sales.data.orders.filter(
    (item) =>
      session.profile.role === "super" ||
      item.storeId === session.profile.storeId,
  );
  const pending = orders.filter((item) => item.status === "pending_approval");
  const names = Object.fromEntries(
    organization.data.accounts.map((item) => [item.id, item.name]),
  );
  const run = async (orderId: string, decision: "approve" | "reject") => {
    if (
      decision === "approve" &&
      !window.confirm("确认批准该例外订单价格与赠课方案？")
    )
      return;
    setError("");
    setNotice("");
    try {
      await actions.approveOrder({
        orderId,
        decision,
        reason: reasons[orderId] ?? "",
      });
      setNotice(
        decision === "approve"
          ? "例外订单已确认，可以收款开通课包。"
          : "例外订单已驳回并取消。",
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "审批失败");
    }
  };
  return (
    <div className="page-stack organization-page">
      <header className="page-heading">
        <div>
          <span>
            {session.profile.role === "super"
              ? "全公司经营与异常闭环"
              : "门店经营与异常闭环"}
          </span>
          <h1>待办</h1>
          <p>
            审批本店优惠、赠课和手工改价订单；审核只能确认或驳回，不修改员工提交值。
          </p>
        </div>
        <StatusBadge tone={pending.length ? "warning" : "success"}>
          {pending.length}笔待审批
        </StatusBadge>
      </header>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="stat-grid">
        <StatCard
          label="例外待审批"
          value={`${pending.length}`}
          detail="优惠/赠课/改价"
        />
        <StatCard
          label="本店已确认"
          value={`${orders.filter((o) => o.status === "confirmed").length}`}
          detail="可以继续收款"
        />
        <StatCard
          label="本店已收款"
          value={`${orders.filter((o) => o.status === "paid").length}`}
          detail="课包已开通"
        />
      </div>
      {pending.length ? (
        <div className="approval-list">
          {pending.map((order) => (
            <Card key={order.id}>
              <div className="approval-order">
                <ClipboardCheck />
                <div>
                  <span>{order.orderNumber}</span>
                  <h2>
                    {names[order.memberId]} · {order.pricing.productName}
                  </h2>
                  <p>
                    标准价 {money(order.pricing.standardPriceCents)} → 成交{" "}
                    {money(order.pricing.finalPriceCents)} · 赠送{" "}
                    {order.pricing.giftLessons} 课时
                  </p>
                  <strong>申请原因：{order.exceptionReason}</strong>
                </div>
                <StatusBadge tone="warning">待审批</StatusBadge>
              </div>
              <label className="v4-field">
                <span>审批意见（驳回时必填）</span>
                <input
                  value={reasons[order.id] ?? ""}
                  onChange={(e) =>
                    setReasons({ ...reasons, [order.id]: e.target.value })
                  }
                  placeholder="确认价格方案或填写驳回原因"
                />
              </label>
              <div className="approval-actions">
                <Button
                  onClick={() => run(order.id, "approve")}
                  disabled={actions.pending}
                >
                  <BadgeCheck size={16} />
                  确认
                </Button>
                <Button
                  variant="danger"
                  onClick={() => run(order.id, "reject")}
                  disabled={actions.pending}
                >
                  <XCircle size={16} />
                  驳回
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="没有例外订单待办"
          description="标准价订单无需审批；员工提交优惠、赠课或改价后会出现在这里。"
        />
      )}
    </div>
  );
}
